import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  canManageSchool,
  useApproveKp,
  useCreateKp,
  useDeprecateKp,
  useExtractKps,
  useKnowledgePoints,
  useMergeKp,
  useOrgMe,
  useUpdateKp,
  type KnowledgePoint,
} from "@/api/school";
import { useAuth } from "@/auth/OidcProvider";
import { useMyEmployees } from "../workbench/EmployeeSwitcher";

/** 知识点管理 — Exam Engine P0 review & convergence UI (spec §5.2/§7).
 *  AI-extracted candidates are approved / merged / deprecated here; axes
 *  (root nodes) drive the student radar, so roots are not deletable. */
export default function KnowledgePointsPage() {
  const { t } = useTranslation();
  const { state } = useAuth();
  const isAdmin = state.status === "authenticated" && state.user.isAdmin;
  const orgMe = useOrgMe();
  const canManage = isAdmin || canManageSchool(orgMe.data);

  const [tab, setTab] = useState<"candidate" | "approved" | "all">("candidate");
  // 学科标签挂钩(Jarod 2026-08-14):列表/抽取/新建全部跟选中学科走,
  // 多学科账号不再永远看英语树。
  const { employees } = useMyEmployees();
  const [pickedSubject, setPickedSubject] = useState<string>("");
  const subjectId = pickedSubject || (employees[0]?.subject_id ?? "");
  const kps = useKnowledgePoints(tab === "all" ? undefined : tab, subjectId || undefined);
  const allKps = useKnowledgePoints(undefined, subjectId || undefined); // for parent/merge pickers
  const approve = useApproveKp();
  const deprecate = useDeprecateKp();
  const [merging, setMerging] = useState<KnowledgePoint | null>(null);
  const [editing, setEditing] = useState<KnowledgePoint | "new" | null>(null);
  const [showExtract, setShowExtract] = useState(false);

  const byId = useMemo(() => {
    const m = new Map<string, KnowledgePoint>();
    for (const k of allKps.data ?? []) m.set(k.id, k);
    return m;
  }, [allKps.data]);

  const roots = (allKps.data ?? []).filter((k) => k.parent_id === null && k.status === "approved");
  const candidateCount = (allKps.data ?? []).filter((k) => k.status === "candidate").length;

  const rows = useMemo(() => {
    // Order: roots first, then children grouped under their parent code.
    const list = [...(kps.data ?? [])];
    return list.sort((a, b) => {
      const pa = a.parent_id ? byId.get(a.parent_id)?.code ?? "" : a.code;
      const pb = b.parent_id ? byId.get(b.parent_id)?.code ?? "" : b.code;
      return pa === pb ? (a.parent_id ? 1 : 0) - (b.parent_id ? 1 : 0) || a.code.localeCompare(b.code) : pa.localeCompare(pb);
    });
  }, [kps.data, byId]);

  const statusBadge: Record<string, string> = {
    candidate: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    merged: "bg-slate-100 text-slate-500 border-slate-200",
    deprecated: "bg-rose-50 text-rose-500 border-rose-200",
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-5 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{t("kp.title")}</h1>
          <p className="text-xs text-slate-500 mt-1">{t("kp.hint")}</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowExtract(true)}
              className="px-3 py-1.5 text-sm font-medium rounded bg-violet-500 text-white hover:bg-violet-600"
            >
              {t("kp.extract")}
            </button>
            <button
              type="button"
              onClick={() => setEditing("new")}
              className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
            >
              {t("kp.add")}
            </button>
          </div>
        )}
      </header>

      <div className="mb-4 flex items-center gap-1">
        {employees.length > 1 && (
          <select
            value={subjectId}
            onChange={(e) => setPickedSubject(e.target.value)}
            className="mr-2 rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            {employees.map((emp: { subject_id: string; name: string }) => (
              <option key={emp.subject_id} value={emp.subject_id}>{emp.name}</option>
            ))}
          </select>
        )}
        {(["candidate", "approved", "all"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-sm rounded ${
              tab === k ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-600"
            }`}
          >
            {t(`kp.tab.${k}`)}
            {k === "candidate" && candidateCount > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-400 text-white text-[10px] px-1.5">{candidateCount}</span>
            )}
          </button>
        ))}
      </div>

      {kps.isLoading && <p className="text-sm text-slate-400">…</p>}
      {!kps.isLoading && rows.length === 0 && (
        <p className="text-sm text-slate-400">{t("kp.empty")}</p>
      )}

      <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
        {rows.map((k) => (
          <div key={k.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
            <span className={k.parent_id ? "pl-6 text-slate-300" : "text-slate-400"}>
              {k.parent_id ? "└" : "◆"}
            </span>
            <div className="min-w-0 flex-1">
              <span className="font-medium text-slate-800">{k.name_zh}</span>
              {k.name_en && <span className="ml-2 text-slate-400">{k.name_en}</span>}
              <span className="ml-2 font-mono text-[11px] text-slate-400">{k.code}</span>
              {k.parent_id && byId.get(k.parent_id) && (
                <span className="ml-2 text-[11px] text-slate-400">
                  → {byId.get(k.parent_id)!.name_zh}
                </span>
              )}
              {k.source_ref && (
                <div className="text-[11px] text-slate-400 truncate">{k.source_ref}</div>
              )}
              {k.proposed_by_name && (
                <div className="text-[11px] text-slate-500">{t("kp.proposedBy", { name: k.proposed_by_name })}</div>
              )}
            </div>
            {k.grade_band && <span className="text-[11px] text-slate-400">{k.grade_band}</span>}
            <span className={`rounded border px-1.5 py-0.5 text-[11px] ${statusBadge[k.status]}`}>
              {t(`kp.status.${k.status}`)}
            </span>
            {canManage && k.status === "candidate" && (
              <>
                <button
                  type="button"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate(k.id)}
                  className="px-2 py-1 text-xs rounded bg-emerald-500 text-white hover:bg-emerald-600"
                >
                  {t("kp.approve")}
                </button>
                <button
                  type="button"
                  onClick={() => setMerging(k)}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  {t("kp.merge")}
                </button>
              </>
            )}
            {canManage && k.status !== "merged" && (
              <button
                type="button"
                onClick={() => setEditing(k)}
                className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                ✎
              </button>
            )}
            {canManage && k.status === "approved" && k.parent_id && (
              <button
                type="button"
                disabled={deprecate.isPending}
                onClick={() => deprecate.mutate(k.id)}
                className="px-2 py-1 text-xs rounded border border-rose-200 text-rose-500 hover:bg-rose-50"
              >
                {t("kp.deprecate")}
              </button>
            )}
          </div>
        ))}
      </div>

      {merging && (
        <MergeModal kp={merging} targets={(allKps.data ?? []).filter((x) => x.status === "approved" && x.id !== merging.id)} onClose={() => setMerging(null)} />
      )}
      {editing && (
        <EditModal kp={editing === "new" ? null : editing} roots={roots} subjectId={subjectId || undefined} onClose={() => setEditing(null)} />
      )}
      {showExtract && <ExtractModal subjectId={subjectId || undefined} onClose={() => setShowExtract(false)} />}
    </div>
  );
}

function MergeModal({ kp, targets, onClose }: { kp: KnowledgePoint; targets: KnowledgePoint[]; onClose: () => void }) {
  const { t } = useTranslation();
  const merge = useMergeKp();
  const hinted = /suggested_merge_into=([A-Z0-9-]+)/.exec(kp.source_ref ?? "")?.[1];
  const [into, setInto] = useState(targets.find((x) => x.code === hinted)?.id ?? "");
  return (
    <Modal title={t("kp.mergeTitle", { name: kp.name_zh })} onClose={onClose}>
      <p className="text-xs text-slate-500 mb-2">{t("kp.mergeHint")}</p>
      <select
        value={into}
        onChange={(e) => setInto(e.target.value)}
        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm mb-4"
      >
        <option value="">—</option>
        {targets.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name_zh} ({x.code}){x.code === hinted ? ` ★ ${t("kp.aiSuggested")}` : ""}
          </option>
        ))}
      </select>
      <ModalFoot
        onClose={onClose}
        disabled={!into || merge.isPending}
        onSave={() => merge.mutate({ id: kp.id, into_id: into }, { onSuccess: onClose })}
        saveLabel={t("kp.merge")}
      />
    </Modal>
  );
}

function EditModal({ kp, roots, subjectId, onClose }: { kp: KnowledgePoint | null; roots: KnowledgePoint[]; subjectId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateKp();
  const update = useUpdateKp();
  const [form, setForm] = useState({
    code: kp?.code ?? "",
    name_zh: kp?.name_zh ?? "",
    name_en: kp?.name_en ?? "",
    parent_id: kp?.parent_id ?? "",
    grade_band: kp?.grade_band ?? "",
  });
  const busy = create.isPending || update.isPending;
  function save() {
    const body = { ...form, name_en: form.name_en || undefined, parent_id: form.parent_id || null, grade_band: form.grade_band || undefined };
    if (kp) update.mutate({ id: kp.id, ...body }, { onSuccess: onClose });
    else create.mutate({ ...body, subject_id: subjectId || undefined }, { onSuccess: onClose });
  }
  return (
    <Modal title={kp ? t("kp.editTitle") : t("kp.add")} onClose={onClose}>
      <div className="space-y-2">
        <Field label={t("kp.fields.nameZh")} value={form.name_zh} onChange={(v) => setForm({ ...form, name_zh: v })} />
        <Field label={t("kp.fields.nameEn")} value={form.name_en} onChange={(v) => setForm({ ...form, name_en: v })} />
        <Field label={t("kp.fields.code")} value={form.code} onChange={(v) => setForm({ ...form, code: v })} mono />
        <label className="block text-sm">
          <span className="text-slate-600 text-xs">{t("kp.fields.parent")}</span>
          <select
            value={form.parent_id}
            onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">{t("kp.fields.parentNone")}</option>
            {roots.map((r) => (
              <option key={r.id} value={r.id}>{r.name_zh} ({r.code})</option>
            ))}
          </select>
        </label>
        <Field label={t("kp.fields.gradeBand")} value={form.grade_band} onChange={(v) => setForm({ ...form, grade_band: v })} placeholder="G7" />
      </div>
      <ModalFoot onClose={onClose} disabled={!form.code.trim() || !form.name_zh.trim() || busy} onSave={save} saveLabel={t("common.save")} />
    </Modal>
  );
}

function ExtractModal({ subjectId, onClose }: { subjectId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const extract = useExtractKps();
  const [text, setText] = useState("");
  const [gradeBand, setGradeBand] = useState("G7");
  const [result, setResult] = useState<string | null>(null);
  return (
    <Modal title={t("kp.extractTitle")} onClose={onClose} wide>
      <p className="text-xs text-slate-500 mb-2">{t("kp.extractHint")}</p>
      <textarea
        rows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("kp.extractPlaceholder")}
        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
      />
      <div className="mt-2 flex items-center gap-2">
        <span className="text-xs text-slate-600">{t("kp.fields.gradeBand")}</span>
        <input value={gradeBand} onChange={(e) => setGradeBand(e.target.value)} className="w-20 rounded border border-slate-300 px-2 py-1 text-sm" />
      </div>
      {result && <p className="mt-2 text-sm text-emerald-600">{result}</p>}
      {extract.isError && <p className="mt-2 text-sm text-rose-500">{String(extract.error)}</p>}
      <ModalFoot
        onClose={onClose}
        disabled={text.trim().length < 20 || extract.isPending}
        onSave={() =>
          extract.mutate(
            { text, grade_band: gradeBand || undefined, subject_id: subjectId || undefined },
            {
              onSuccess: (r) => {
                const d = r as { inserted: number; skipped: number };
                setResult(t("kp.extractDone", { inserted: d.inserted, skipped: d.skipped }));
              },
            },
          )
        }
        saveLabel={extract.isPending ? t("kp.extracting") : t("kp.runExtract")}
      />
    </Modal>
  );
}

function Modal({ title, wide, onClose, children }: { title: string; wide?: boolean; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-lg bg-white p-5 shadow-xl`} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ModalFoot({ onClose, onSave, disabled, saveLabel }: { onClose: () => void; onSave: () => void; disabled: boolean; saveLabel: string }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 flex justify-end gap-2">
      <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-200 text-slate-600">
        {t("common.cancel")}
      </button>
      <button type="button" disabled={disabled} onClick={onSave} className="px-3 py-1.5 text-sm rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50">
        {saveLabel}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, mono, placeholder }: { label: string; value: string; onChange: (v: string) => void; mono?: boolean; placeholder?: string }) {
  return (
    <label className="block text-sm">
      <span className="text-slate-600 text-xs">{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}
